import {
  ApiBlueprint,
  PageBlueprint,
  createApiFactory,
  createFrontendPlugin,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/frontend-plugin-api';
import {
  compatWrapper,
  convertLegacyRouteRef,
} from '@backstage/core-compat-api';
import { RiHammerLine } from '@remixicon/react';

import {
  AdminClient,
  adminApiRef,
  ArmoryClient,
  armoryApiRef,
  BulkActionsClient,
  bulkActionsApiRef,
  BulkCloneClient,
  bulkCloneApiRef,
} from './api';
import { rootRouteRef } from './routes';

const armoryApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: armoryApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new ArmoryClient({ discoveryApi, fetchApi }),
      }),
    ),
});

// Bulk Actions talks to its own /bulk routes, so it gets its own client.
const bulkActionsApi = ApiBlueprint.make({
  name: 'bulk-actions',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: bulkActionsApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new BulkActionsClient({ discoveryApi, fetchApi }),
      }),
    ),
});

// Bulk Clone's client for the Kamino backend (called as the signed-in user).
const bulkCloneApi = ApiBlueprint.make({
  name: 'bulk-clone',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: bulkCloneApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new BulkCloneClient({ discoveryApi, fetchApi }),
      }),
    ),
});

// Armory Admin: who can see and use each tool, groups, pause and the audit log.
const adminApi = ApiBlueprint.make({
  name: 'admin',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: adminApiRef,
        deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
        factory: ({ discoveryApi, fetchApi }) =>
          new AdminClient({ discoveryApi, fetchApi }),
      }),
    ),
});

const armoryPage = PageBlueprint.make({
  params: {
    path: '/armory',
    routeRef: convertLegacyRouteRef(rootRouteRef),
    loader: () =>
      import('./components/ArmoryHome').then(m =>
        compatWrapper(<m.ArmoryHome />),
      ),
  },
});

// The Workflow Builder tool page, reached from its tile on the landing page. It declares
// no routeRef, so the app derives no second sidebar item for it (the tile is the entry).
const workflowBuilderPage = PageBlueprint.make({
  name: 'workflow-builder',
  params: {
    path: '/armory/workflow-builder',
    loader: () =>
      Promise.all([
        import('./components/shared'),
        import('./components/WorkflowBuilder'),
      ]).then(([{ ToolGate }, { WorkflowBuilder }]) =>
        compatWrapper(
          <ToolGate toolId="workflow-builder">
            <WorkflowBuilder />
          </ToolGate>,
        ),
      ),
  },
});

// The Bulk Actions hub, reached from its tile like the Workflow Builder.
const bulkActionsPage = PageBlueprint.make({
  name: 'bulk-actions',
  params: {
    path: '/armory/bulk-actions',
    loader: () =>
      Promise.all([
        import('./components/shared'),
        import('./components/BulkActions'),
      ]).then(([{ ToolGate }, { BulkActions }]) =>
        compatWrapper(
          <ToolGate toolId="bulk-actions">
            <BulkActions />
          </ToolGate>,
        ),
      ),
  },
});

// The Bulk Clone tool page, reached from its tile like the Workflow Builder.
const bulkClonePage = PageBlueprint.make({
  name: 'bulk-clone',
  params: {
    path: '/armory/bulk-clone',
    loader: () =>
      Promise.all([
        import('./components/shared'),
        import('./components/BulkClone'),
      ]).then(([{ ToolGate }, { BulkClone }]) =>
        compatWrapper(
          <ToolGate toolId="bulk-clone">
            <BulkClone />
          </ToolGate>,
        ),
      ),
  },
});

// The Admin page, linked from the home page for owners and admins. It checks the role
// itself, and the backend checks every call.
const adminPage = PageBlueprint.make({
  name: 'admin',
  params: {
    path: '/armory/admin',
    loader: () =>
      import('./components/Admin').then(m => compatWrapper(<m.AdminPage />)),
  },
});

/**
 * The Armory — a hub of demo-engineering tools, on the Backstage new frontend
 * system.
 *
 * @public
 */
export default createFrontendPlugin({
  pluginId: 'armory',
  title: 'The Armory',
  icon: <RiHammerLine />,
  // Per-user opt-in toggles (Settings > Feature Flags). NOT an access gate — that's the
  // levels set on the Admin page. Reserved for previewing a tool's in-progress
  // variant per-user once it's live; wire one to a tile via Tool.featureFlag when needed.
  featureFlags: [
    {
      name: 'armory-workflow-builder',
      description: 'Preview in-progress Workflow Builder changes.',
    },
  ],
  routes: {
    root: convertLegacyRouteRef(rootRouteRef),
  },
  extensions: [
    armoryApi,
    bulkActionsApi,
    bulkCloneApi,
    adminApi,
    armoryPage,
    workflowBuilderPage,
    bulkActionsPage,
    bulkClonePage,
    adminPage,
  ],
});
